// src/TeacherMagicRequest.js
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from './api';
import VisitOMSLogo from './VisitOMS_logo.png';

const TeacherMagicRequest = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const trimmedEmail = useMemo(() => (email || '').trim().toLowerCase(), [email]);
  const canSubmit = !submitting && !!trimmedEmail;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!trimmedEmail) {
      setError('Please enter your work email address.');
      return;
    }

    setSubmitting(true);
    try {
      await API.post('/auth/teacher-magic-request', { email: trimmedEmail });
      setDone(true);
    } catch (err) {
      console.error('Teacher magic request error:', err);
      // Security UX: same response either way
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100">
      <div className="min-h-screen flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {/* top accent */}
            <div className="h-2 bg-gradient-to-r from-violet-600 via-indigo-600 to-sky-600" />

            <div className="p-6 sm:p-7">
              {/* BRAND HEADER */}
              <div className="flex flex-col items-center justify-center mb-6">
                <img
                  src={VisitOMSLogo}
                  alt="VisitOMS logo"
                  className="w-14 h-14 sm:w-16 sm:h-16 mb-3 object-contain"
                />
                <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 text-center">
                  Provider portal access
                </h2>
                <p className="text-[12px] text-slate-600 mt-2 text-center leading-snug">
                  Teachers and admins can request a secure login link by email.
                </p>

                <div className="mt-3 inline-flex items-center gap-2">
                  <span className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 font-semibold">
                    Magic Link
                  </span>
                  <span className="text-[11px] text-slate-500">Work email</span>
                </div>
              </div>

              {!done && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* EMAIL */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Work email address <span className="text-rose-600">*</span>
                    </label>
                    <input
                      type="email"
                      className="w-full p-3.5 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                      placeholder="you@agency.org"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      inputMode="email"
                      required
                    />
                    <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">
                      Use the same email your agency uses for VisitOMS EasyCare.
                    </p>
                  </div>

                  {/* ERROR */}
                  {error && (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2">
                      <p className="text-rose-700 text-sm font-semibold">Fix this</p>
                      <p className="text-rose-700 text-xs mt-0.5">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className={`w-full py-3.5 rounded-2xl text-sm font-extrabold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      canSubmit
                        ? 'bg-sky-600 text-white hover:bg-sky-700 focus:ring-sky-500'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    {submitting ? 'Sending secure link…' : 'Send secure login link'}
                  </button>

                  <div className="pt-2 text-center">
                    <button
                      type="button"
                      onClick={() => navigate('/parent-login')}
                      className="text-xs font-semibold text-sky-700 hover:text-sky-800 hover:underline"
                    >
                      Parent – request login link
                    </button>
                  </div>
                </form>
              )}

              {done && (
                <div className="text-center">
                  <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                    <span className="text-emerald-700 font-extrabold">✓</span>
                  </div>

                  <h3 className="text-base font-extrabold text-slate-900 mb-2">
                    Check your email
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 leading-snug">
                    If this email is registered as a teacher or admin, a secure login link has been sent.
                    Please check your inbox and spam/junk folder.
                  </p>

                  <button
                    onClick={() => navigate('/login')}
                    className="w-full py-3 rounded-2xl text-sm font-extrabold bg-slate-100 text-slate-800 hover:bg-slate-200"
                  >
                    Back to standard login
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setDone(false);
                      setError('');
                      setSubmitting(false);
                    }}
                    className="mt-3 w-full py-3 rounded-2xl text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  >
                    Send another link
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* bottom helper */}
          <div className="mt-4 text-center text-[11px] text-slate-500 px-2 leading-snug">
            For security, we don&apos;t confirm whether an email exists in the system.
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherMagicRequest;


